import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import { PO_MENU_TITLE } from "@/modules/inventory/presentation/ui/purchase/purchaseLaoLabels";
import { TRANSFER_SECTION_STACK } from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { PurchaseOrderReportParams } from "@/modules/reports/presentation/api/client";
import { reportsApi } from "@/modules/reports/presentation/api/client";
import { usePurchaseOrderReportSummaryQuery } from "@/modules/reports/presentation/api/queries";
import { PurchaseOrderReportsContent } from "@/modules/reports/presentation/ui/purchase-order/PurchaseOrderReportsContent";
import { downloadPurchaseOrderReportCsv } from "@/modules/reports/presentation/ui/purchase-order/export-purchase-order-report-csv";
import {
  PurchaseOrderReportsFilter,
  type PurchaseOrderReportsSearch,
} from "@/modules/reports/presentation/ui/purchase-order/PurchaseOrderReportsFilter";
import { PurchaseOrderReportsSummaryCards } from "@/modules/reports/presentation/ui/purchase-order/PurchaseOrderReportsSummaryCards";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useSearch } from "@tanstack/react-router";
import { ClipboardListIcon, FileDownIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export function PurchaseOrderReportsPage() {
  const search = useSearch({
    from: "/app/reports/inventory/purchase-orders",
  }) as PurchaseOrderReportsSearch;
  const [exporting, setExporting] = useState(false);

  const reportParams: PurchaseOrderReportParams = useMemo(
    () => ({
      orderDateFrom: search.orderDateFrom,
      orderDateTo: search.orderDateTo,
      supplierId: search.supplierId,
    }),
    [search.orderDateFrom, search.orderDateTo, search.supplierId],
  );

  const summaryQuery = usePurchaseOrderReportSummaryQuery(reportParams);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await reportsApi.getPurchaseOrderReportExport(reportParams);
      if (
        data.bySupplier.length === 0 &&
        data.summary.openReceivablePoCount === 0
      ) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }
      downloadPurchaseOrderReportCsv(data);
      toast.success(
        `ສົ່ງອອກ CSV: ${data.bySupplier.length.toLocaleString("lo-LA")} ຜູ້ສະໜອງ`,
      );
    } catch {
      toast.error("ສົ່ງອອກລົ້ມເຫຼວ");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ລາຍງານສາງ", to: "/app/reports/inventory" },
            { label: `ລາຍງານ ${PO_MENU_TITLE}` },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <ClipboardListIcon className="size-5" />
              ລາຍງານ {PO_MENU_TITLE}
            </h2>
            <p className="text-muted-foreground">
              PO ຄ້າງຮັບ · ມູນຄ່າຄ້າງ · ສະຫຼຸບຕາມຜູ້ສະໜອງ · CSV
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={() => void handleExport()}
            className="gap-2"
          >
            <FileDownIcon className="size-4" />
            {exporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກ CSV"}
          </Button>
        </div>

        <div className={TRANSFER_SECTION_STACK}>
          <PurchaseOrderReportsFilter />

          <PurchaseOrderReportsSummaryCards
            summary={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />

          <PurchaseOrderReportsContent reportParams={reportParams} />
        </div>
      </Main>
    </>
  );
}
